/**
 * Feature Flag Context - manages feature flags across the application
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSearchParams, useNavigate as useRouterNavigate } from 'react-router-dom';

interface FeatureFlags {
  home: boolean;
}

interface FeatureFlagContextType {
  flags: FeatureFlags;
  isHomePageEnabled: boolean;
  getHomeRoute: () => string;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();

  // Initialize state directly from URL params (synchronous - avoids timing issues)
  // Once set to true, it stays true for the session
  const [flags, setFlags] = useState<FeatureFlags>(() => ({
    home: searchParams.get('home') === 'true',
  }));

  // Update flags if URL params change (e.g., user manually edits URL or shares link)
  // Only update if the flag is being turned ON (once on, stays on for session)
  useEffect(() => {
    const urlHasHomeFlag = searchParams.get('home') === 'true';
    if (urlHasHomeFlag && !flags.home) {
      setFlags({ home: true });
    }
  }, [searchParams, flags.home]);

  const isHomePageEnabled = flags.home;

  const getHomeRoute = () => {
    return isHomePageEnabled ? '/?home=true' : '/environment';
  };

  return (
    <FeatureFlagContext.Provider value={{ flags, isHomePageEnabled, getHomeRoute }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

/**
 * Hook to access feature flags
 */
export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

/**
 * Custom navigate hook that preserves feature flags in URLs
 */
export function useFeatureNavigate() {
  const navigate = useRouterNavigate();
  const { flags } = useFeatureFlags();

  return (to: string | number, options?: { replace?: boolean; state?: any }) => {
    // If navigating to a number (back/forward), use as-is
    if (typeof to === 'number') {
      navigate(to);
      return;
    }

    // Parse the target URL
    const [pathnamePart, existingQuery] = to.split('?');
    const pathname = pathnamePart || '/';

    // Build search params from active flags
    const searchParams = new URLSearchParams(existingQuery);

    // Add feature flags to search params if not already present
    if (flags.home && !searchParams.has('home')) {
      searchParams.set('home', 'true');
    }

    // Navigate using pathname + search params
    const search = searchParams.toString();
    navigate(
      {
        pathname,
        search: search ? `?${search}` : '',
      },
      options
    );
  };
}
